import { jwtDecode } from 'jwt-decode';
import { type Profile } from 'oidc-client';
import React, { createContext, type Dispatch, useContext, useEffect, useReducer } from 'react';
import { DEFAULT_IDP } from '../helpers/constants';

type UserManagerProviderProps = { children: React.ReactNode };
type Auth = boolean;
type currentSchema = string;
type schemas = string;
type controlledUsername = string;

type User = {
  preferred_username?: string;
  schemas?: schemas; // list of schema codes separaed by comma
  currentSchema?: currentSchema; // schema code
  superuser: string;
  controlledUsername?: controlledUsername;
};
type Tokens = {
  id_token: string;
  access_token: string;
  expires_at: number;
  refresh_token?: string;
  auth_time?: number;
};
type LoginStatus = 'idp' | 'login' | 'schema' | 'done';
type UserState = {
  auth: Auth;
  user?: User;
  tokens?: Tokens;
  loginStatus?: LoginStatus;
  idp?: string;
  isDefaultIdp?: boolean;
};
type LoginJwt = {
  token: string;
  schema: string;
  idp?: string;
};

export const UserActionTypes = {
  UpdateUser: 0,
  UpdateAuth: 1,
  UpdateLoginStatus: 2,
  UpdateCurrentSchema: 3,
  UpdateSchemas: 4,
  UpdateControlledUsername: 5,
  UpdateLoginJwt: 6,
  Reset: 7,
} as const;
export type UserActionTypes = (typeof UserActionTypes)[keyof typeof UserActionTypes];

export interface UpdateUser {
  type: typeof UserActionTypes.UpdateUser;
  payload: UserState;
}
export interface Reset {
  type: typeof UserActionTypes.Reset;
}
export interface UpdateSchemas {
  type: typeof UserActionTypes.UpdateSchemas;
  payload: schemas;
}
export interface UpdateAuth {
  type: typeof UserActionTypes.UpdateAuth;
  payload: Auth;
}
export interface UpdateLoginStatus {
  type: typeof UserActionTypes.UpdateLoginStatus;
  payload: LoginStatus;
}
export interface UpdateCurrentSchema {
  type: typeof UserActionTypes.UpdateCurrentSchema;
  payload: currentSchema;
}
export interface UpdateControlledUsername {
  type: typeof UserActionTypes.UpdateControlledUsername;
  payload: controlledUsername;
}
export interface UpdateLoginJwt {
  type: typeof UserActionTypes.UpdateLoginJwt;
  payload: LoginJwt;
}
export type UserReducerActions =
  | UpdateUser
  | Reset
  | UpdateAuth
  | UpdateLoginStatus
  | UpdateCurrentSchema
  | UpdateSchemas
  | UpdateControlledUsername
  | UpdateLoginJwt;

const initialState: UserState = {
  auth: false,
};

const localState = JSON.parse(localStorage.getItem('UserState') || '{}');

export const userReducer = (state: UserState, action: UserReducerActions) => {
  switch (action.type) {
    case UserActionTypes.UpdateUser:
      return {
        ...state,
        ...action.payload,
      };
    case UserActionTypes.UpdateSchemas: {
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            schemas: action.payload,
          },
        };
      }
      return state;
    }
    case UserActionTypes.UpdateAuth:
      return {
        ...state,
        auth: action.payload,
      };
    case UserActionTypes.UpdateLoginStatus:
      return {
        ...state,
        loginStatus: action.payload,
      };
    case UserActionTypes.UpdateCurrentSchema:
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            currentSchema: action.payload,
          },
        };
      }
      return state;
    case UserActionTypes.UpdateControlledUsername:
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            controlledUsername: action.payload,
          },
        };
      }
      return state;
    case UserActionTypes.UpdateLoginJwt: {
      const { idp, schema, token } = action.payload;
      const _idp = idp ?? DEFAULT_IDP;
      const decodedToken: Profile = jwtDecode(token);
      // Persist opaque user ID for Sentry (matches LoginCallback path).
      if (decodedToken.sub) {
        localStorage.setItem('userId', decodedToken.sub);
      } else {
        localStorage.removeItem('userId');
      }
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          currentSchema: schema,
          schemas: decodedToken['custom:schema'],
          preferred_username: decodedToken['preferred_username'],
          superuser: decodedToken['custom:superuser'],
        },
        tokens: {
          ...state.tokens,
          id_token: token,
          expires_at: decodedToken['exp'],
          access_token: '', // we don't have an access token
        },
        loginStatus: 'done',
        idp: _idp,
        isDefaultIdp: _idp === DEFAULT_IDP,
      } as UserState;
    }
    case UserActionTypes.Reset:
      localStorage.removeItem('UserState');
      localStorage.removeItem('userId');
      return {
        ...initialState,
      };
    default:
      throw new Error('Unexpected action');
  }
};

export const UserContext = createContext<{ state: UserState; dispatch: Dispatch<UserReducerActions> }>({
  state: initialState,
  dispatch: () => undefined,
});

export const UserProvider = ({ children }: UserManagerProviderProps) => {
  const [state, dispatch] = useReducer(userReducer, localState || initialState);
  useEffect(() => {
    localStorage.setItem('UserState', JSON.stringify(state));
  }, [state]);
  return <UserContext.Provider value={{ state, dispatch }}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserManagerProvider');
  }
  return context;
};
